import DeleteIcon from '@mui/icons-material/Delete';
import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { experimentsService } from '../../../Services/Experiments.services';
import styles from './ExperimentsList.module.css';
import ExperimentStatus from './ExperimentStatus';

function ExperimentsList({ experiments, onExperimentDeleted }) {
    const navigate = useNavigate();
    const { isAdmin, isEvaluator } = useSelector((state) => state.auth);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [experimentToDelete, setExperimentToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleViewEvaluation = (id, e) => {
        e.stopPropagation();
        navigate(`/experiments/${id}/evaluation`);
    };

    const handleDeleteClick = (e, experiment) => {
        e.stopPropagation();
        setExperimentToDelete(experiment);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!experimentToDelete) return;

        setIsDeleting(true);
        try {
            await experimentsService.deleteExperiment(experimentToDelete.id);
            setDeleteDialogOpen(false);
            setExperimentToDelete(null);

            if (onExperimentDeleted) {
                onExperimentDeleted();
            }
        } catch (error) {
            console.error('Error deleting experiment:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setTimeout(() => setExperimentToDelete(null), 1000);
    };

    const handleViewRuns = (e, experiment) => {
        e.stopPropagation();
        navigate(`/experiments/${experiment.id}/runs`);
    };

    const handleViewDocs = (e, experiment) => {
        e.stopPropagation();
        navigate(`/experiments/${experiment.id}/docs`);
    };

    const handleViewResults = (e, experiment) => {
        e.stopPropagation();
        navigate(`/experiments/${experiment.id}/results`);
    };

    const handleViewStatus = (e, experiment) => {
        e.stopPropagation();
        navigate(`/experiments/${experiment.id}/status`);
    };

    const adminButtons = [
        { label: 'View Docs', onClick: handleViewDocs },
        { label: 'View Runs', onClick: handleViewRuns },
        { label: 'Results', onClick: handleViewResults },
        { label: 'Progress', onClick: handleViewStatus },
    ];

    return (
        <div className={styles.experiments}>
            {experiments.length === 0
                ? (
                    <Stack sx={{ width: '100%' }} spacing={2}>
                        <Alert severity="warning">Records not found!</Alert>
                    </Stack>
                )
                : experiments.map((experiment) => (
                    <Card
                        key={experiment.id}
                        sx={{
                            minWidth: 270,
                            width: 315,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}
                    >
                        <CardContent sx={{ pb: 0, flexGrow: 1 }}>
                            <div className={styles.cardHeader}>
                                <Typography
                                    variant="subtitle1"
                                    noWrap
                                    title={experiment.name}
                                    sx={{
                                        fontWeight: 'bold',
                                        cursor: isAdmin ? 'pointer' : 'default',
                                        color: experiment?.name ? 'inherit' : 'silver',
                                    }}
                                    onClick={isAdmin ? (e) => handleViewDocs(e, experiment) : undefined}
                                >
                                    {experiment.name ?? 'Unnamed'}
                                </Typography>
                                <div className={styles.cardStatus}>
                                    <ExperimentStatus status={experiment.status} />
                                    {isAdmin && (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(e) => handleDeleteClick(e, experiment)}
                                            aria-label="delete experiment"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                        <CardContent
                            sx={{
                                pt: 1,
                                pb: '8px !important',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <div className={styles.buttonsColumn}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={(e) => handleViewEvaluation(experiment.id, e)}
                                >
                                    Evaluate
                                </Button>
                                {isAdmin && (
                                    <div className={styles.buttons}>
                                        {adminButtons.map((btn) => (
                                            <Button
                                                key={btn.label}
                                                variant="outlined"
                                                size="small"
                                                sx={{ flex: '1 0 0', minWidth: 120 }}
                                                onClick={(e) => btn.onClick(e, experiment)}
                                            >
                                                {btn.label}
                                          </Button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Experiment
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {
                            `Are you sure you want to delete 
                        ${experimentToDelete?.name ? `"${experimentToDelete?.name}"` : 'noname'} 
                        experiment? This action cannot be undone, and all associated data will be permanently removed.`
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDeleteCancel}
                        color="primary"
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        autoFocus
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

ExperimentsList.propTypes = {
    experiments: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            description: PropTypes.string,
            date: PropTypes.string,
            lastRun: PropTypes.string,
            totalRuns: PropTypes.number,
            status: PropTypes.string,
        }),
    ).isRequired,
    onExperimentDeleted: PropTypes.func,
};

ExperimentsList.defaultProps = {
    onExperimentDeleted: null,
};

export default ExperimentsList;
